import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { ExpertCard, PartnerCard } from "../components/Cards";
import { Search, Filter, MapPin, LocateFixed, X } from "lucide-react";

export function ExpertsList() {
  const [params, setParams] = useSearchParams();
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const q = params.get("q") || "";
  const category = params.get("category") || "";

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    api.get(`/experts?${p.toString()}`).then((r) => {
      if (active) setExperts(r.data);
    }).catch(() => {
      if (active) { setExperts([]); setError(true); }
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [q, category]);

  const setParam = (k, v) => { const n = new URLSearchParams(params); v ? n.set(k, v) : n.delete(k); setParams(n); };

  const cats = ["3D Designer", "CAD Designer", "Product Designer", "3D Artist", "Mechanical Designer", "Engineer"];

  return (
    <div data-testid="experts-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <div className="tl-label mb-2">Discover</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4">Find a 3D Expert</h1>
        <p className="text-[#475569] max-w-2xl">Browse verified experts from around the world. Filter by skill, location and specialization.</p>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
          <input data-testid="experts-search" defaultValue={q} onKeyDown={(e) => e.key === "Enter" && setParam("q", e.target.value)}
            placeholder="What do you need?" className="tl-input pl-10" />
        </div>
        <select data-testid="experts-category-filter" value={category} onChange={(e) => setParam("category", e.target.value)} className="tl-input md:max-w-xs">
          <option value="">All Categories</option>
          {cats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-20 text-[#475569]">Loading experts…</div>
      ) : error ? (
        <div data-testid="experts-load-error" className="tl-alert max-w-xl">We couldn’t load experts right now. Please try again in a moment.</div>
      ) : experts.length === 0 ? (
        <div data-testid="no-experts" className="text-center py-20">
          <div className="text-2xl font-display font-semibold mb-2">No experts found</div>
          <div className="text-[#475569]">Try changing your filters or search terms.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {experts.map((e) => <ExpertCard key={e.id} expert={e} />)}
        </div>
      )}
    </div>
  );
}

export function PartnersList() {
  const [partners, setPartners] = useState([]);
  const [q, setQ] = useState("");
  const [tech, setTech] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [coords, setCoords] = useState(null); // { lat, lng, label }
  const [cityInput, setCityInput] = useState("");
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoErr, setGeoErr] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true); setError(false);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (tech) p.set("tech", tech);
    if (coords) { p.set("lat", coords.lat); p.set("lng", coords.lng); }
    api.get(`/partners?${p.toString()}`).then((r) => { if (active) setPartners(r.data); }).catch(() => { if (active) { setPartners([]); setError(true); } }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [q, tech, coords]);

  const useMyLocation = () => {
    setGeoErr(""); setGeoBusy(true);
    if (!navigator.geolocation) { setGeoErr("Your browser doesn't support geolocation"); setGeoBusy(false); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: "Your location" });
        setGeoBusy(false);
      },
      (err) => {
        setGeoErr(err.code === 1 ? "Location permission denied" : "Couldn't detect location, try the city search");
        setGeoBusy(false);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  const searchCity = async (e) => {
    e.preventDefault();
    if (!cityInput.trim()) return;
    setGeoErr(""); setGeoBusy(true);
    try {
      const { data } = await api.get(`/geo/search?q=${encodeURIComponent(cityInput.trim())}`);
      const hit = (data.results || [])[0];
      if (!hit) { setGeoErr("Location not found — try a broader term"); setGeoBusy(false); return; }
      setCoords({ lat: hit.lat, lng: hit.lng, label: hit.label });
    } catch (err) {
      setGeoErr("Geocoding is unavailable right now");
    } finally { setGeoBusy(false); }
  };

  const clearLocation = () => { setCoords(null); setCityInput(""); setGeoErr(""); };

  return (
    <div data-testid="partners-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <div className="tl-label mb-2">Manufacture</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4">Find a 3D Printer Owner</h1>
        <p className="text-[#475569] max-w-2xl">Verified 3D printer owners across FDM, SLA, Resin, and industrial technologies.</p>
      </div>

      {/* Near-me controls */}
      <div className="tl-card p-4 mb-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 flex flex-col md:flex-row gap-2">
            <button
              data-testid="partners-use-location"
              type="button"
              onClick={useMyLocation}
              disabled={geoBusy}
              className="tl-btn-secondary shrink-0 whitespace-nowrap disabled:opacity-50"
            >
              <LocateFixed className="w-4 h-4" />
              <span>{geoBusy && !cityInput ? "Locating…" : "Use my location"}</span>
            </button>
            <form onSubmit={searchCity} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
                <input
                  data-testid="partners-city-input"
                  value={cityInput}
                  onChange={(e) => setCityInput(e.target.value)}
                  placeholder="Or search a city, pincode, area…"
                  className="tl-input pl-10 w-full"
                />
              </div>
              <button data-testid="partners-city-submit" type="submit" disabled={geoBusy} className="tl-btn-secondary shrink-0 disabled:opacity-50">Search</button>
            </form>
          </div>
        </div>
        {coords && (
          <div data-testid="partners-active-location" className="mt-3 flex items-center gap-2 text-xs">
            <span className="tl-chip flex items-center gap-1 !bg-[#0891B2]/15 !text-[#0891B2] border-[#0891B2]/30">
              <MapPin className="w-3 h-3" /> Sorted by distance from: <span className="max-w-[220px] truncate">{coords.label}</span>
            </span>
            <button data-testid="partners-clear-location" onClick={clearLocation} className="text-[#475569] hover:text-slate-900 flex items-center gap-1 text-xs"><X className="w-3 h-3" />Clear</button>
          </div>
        )}
        {geoErr && <div data-testid="partners-geo-error" className="mt-2 text-xs text-red-600">{geoErr}</div>}
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-8">
        <input data-testid="partners-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="What do you want to print?" className="tl-input flex-1" />
        <select data-testid="partners-tech-filter" value={tech} onChange={(e) => setTech(e.target.value)} className="tl-input md:max-w-xs">
          <option value="">All Technologies</option>
          <option value="FDM">FDM</option>
          <option value="SLA">SLA</option>
          <option value="Resin">Resin</option>
          <option value="Industrial">Industrial</option>
        </select>
      </div>
      {loading ? <div data-testid="partners-loading" className="text-center py-20 text-[#475569]">Loading 3D printer owners…</div> : error ? <div data-testid="partners-load-error" className="tl-alert max-w-xl">We couldn't load 3D printer owners right now. Please try again in a moment.</div> : partners.length === 0 ? <div data-testid="no-partners" className="text-center py-20"><div className="text-2xl font-display font-semibold mb-2">No 3D printer owners yet</div><div className="text-[#475569]">Check back soon or invite one to join.</div></div> : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {partners.map((p) => <PartnerCard key={p.id} partner={p} />)}
      </div>}
    </div>
  );
}
